/* Keep a floating panel (dropdown / popover / menu) inside the viewport.
 *
 *   const anchorRef = React.useRef(null);
 *   const pos = useAnchoredPosition(anchorRef, open, { align:'end', width:260 });
 *   …
 *   <div ref={anchorRef} style={{position:'relative'}}>
 *     <button>…</button>
 *     {open && (
 *       <div style={{ ...basePanelStyle, ...(pos || HIDDEN) }}>…</div>
 *     )}
 *   </div>
 *
 * Returns a `position:fixed` style object to spread onto the panel. It
 * anchors below the trigger by default and flips above when there isn't
 * room; horizontally it aligns to one edge of the trigger and then shifts
 * inward so the panel never spills off-screen (i.e. opens toward the left
 * when the trigger sits near the right edge). `maxHeight` is clamped to the
 * space available on the chosen side, so a tall menu scrolls instead of
 * overflowing. Recomputes on scroll/resize while open.
 *
 * The panel should still live INSIDE the trigger's wrapper in the DOM:
 * fixed positioning escapes any `overflow:hidden` ancestor while keeping
 * the panel a descendant, so outside-click `contains()` checks keep working.
 *
 * Options:
 *   gutter        min px gap from any viewport edge              (default 8)
 *   offset        px gap between trigger and panel               (default 6)
 *   align         'start' | 'end' — trigger edge the panel
 *                 aligns to before clamping                      (default 'start')
 *   placement     'bottom' | 'top' — preferred vertical side     (default 'bottom')
 *   width         fixed panel width (px)
 *   matchWidth    stretch the panel to the trigger's width       (default false)
 *   maxHeight     hard cap on height (also clamped to free space)
 *   flipThreshold min free px on the preferred side before
 *                 flipping to the other side                     (default 240)
 */
import React from 'react';

export const HIDDEN_ANCHORED_STYLE = { position: 'fixed', visibility: 'hidden', top: 0, left: 0 };

export function useAnchoredPosition(anchorRef, open, options = {}) {
  const {
    gutter = 8, offset = 6, align = 'start', placement = 'bottom',
    width, matchWidth = false, maxHeight: maxHeightCap, flipThreshold = 240,
  } = options;
  const [pos, setPos] = React.useState(null);

  React.useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next = { position: 'fixed' };

      // Horizontal. With a known width, align to an edge then clamp so the
      // panel stays fully inside the viewport — this is what makes it open
      // toward the left when the trigger is near the right edge.
      let w = matchWidth ? r.width : width;
      if (w != null) {
        w = Math.min(w, vw - gutter * 2);
        let left = align === 'end' ? r.right - w : r.left;
        if (left + w > vw - gutter) left = vw - w - gutter;
        if (left < gutter) left = gutter;
        next.left = Math.round(left);
        next.width = Math.round(w);
        next.right = 'auto';
      } else if (align === 'end') {
        next.right = Math.max(gutter, Math.round(vw - r.right));
        next.left = 'auto';
      } else {
        next.left = Math.max(gutter, Math.min(Math.round(r.left), vw - gutter));
        next.right = 'auto';
      }

      // Vertical. Prefer the requested side; flip to the other when the
      // preferred side is cramped and the opposite has more room.
      const spaceBelow = vh - r.bottom - offset - gutter;
      const spaceAbove = r.top - offset - gutter;
      const up = placement === 'top'
        ? !(spaceAbove < flipThreshold && spaceBelow > spaceAbove)
        : (spaceBelow < flipThreshold && spaceAbove > spaceBelow);
      // Pin exactly one vertical edge and explicitly release the other to
      // 'auto'. Panels may carry a CSS `top`/`bottom` of their own (e.g.
      // `.taxo__menu { top: calc(100% + 4px) }`); if we only set `bottom`
      // here that CSS `top` survives, the box is pinned on BOTH edges and
      // collapses to a sliver. Releasing the unused side guarantees the
      // inline placement always wins — this is what made the bottom-most
      // pickers' menus render ~2px tall when they flipped above the field.
      if (up) {
        next.bottom = Math.round(vh - r.top + offset);
        next.top = 'auto';
        next.maxHeight = Math.max(0, Math.round(Math.min(maxHeightCap ?? Infinity, spaceAbove)));
      } else {
        next.top = Math.round(r.bottom + offset);
        next.bottom = 'auto';
        next.maxHeight = Math.max(0, Math.round(Math.min(maxHeightCap ?? Infinity, spaceBelow)));
      }
      setPos(next);
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchorRef, gutter, offset, align, placement, width, matchWidth, maxHeightCap, flipThreshold, options.recomputeKey]);

  return pos;
}
